import React, { useEffect, useRef } from 'react';

// Set at build time, same pattern as REACT_APP_API_URL/
// REACT_APP_TURNSTILE_SITE_KEY. Unlike Turnstile there is no "always
// passes on any hostname" public test Client ID — a real one (with
// http://localhost:3000 added as an authorized JavaScript origin in
// Google Cloud Console) is needed to exercise this locally.
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

interface GoogleSignInButtonProps {
  onCredential: (idToken: string) => void;
}

// Explicit init+render, not a bare `<div class="g_id_signin">` auto-scan:
// same React-SPA pitfall as TurnstileService's widget (Register.tsx) — the
// GIS script tag is async/defer and can finish loading (and auto-scan the
// DOM) before this component has mounted its container div, silently
// leaving the button unrendered. Polling briefly for `window.google`
// before rendering sidesteps that race.
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onCredential }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const renderButton = () => {
      if (cancelled) return;
      const google = (window as any).google;
      if (google?.accounts?.id && containerRef.current) {
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: { credential: string }) => onCredentialRef.current(response.credential)
        });
        google.accounts.id.renderButton(containerRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with'
        });
      } else {
        pollTimer = setTimeout(renderButton, 100);
      }
    };
    renderButton();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;

  return <div ref={containerRef} className="flex justify-center" />;
};

export default GoogleSignInButton;
